import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Calendar, User, Building, MapPin, DollarSign, FileText, Download, Mail, Phone, Plus, Users, Archive } from 'lucide-react';
import AddCandidatureModal from './AddCandidatureModal';
import AddToVivierModal from './AddToVivierModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const JobOfferDetailPage = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [offer, setOffer] = useState(null);
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVivierModal, setShowVivierModal] = useState(false);
  const [selectedCandidature, setSelectedCandidature] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationPassword, setValidationPassword] = useState('');
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    fetchOfferDetails();
    fetchCandidatures();
    
    // Set up polling for candidatures updates every 30 seconds
    const interval = setInterval(() => {
      fetchCandidatures();
    }, 30000);
    
    setPollingInterval(interval);
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [offerId]);

  const fetchOfferDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const offerData = await response.json();
        setOffer(offerData);
      } else {
        toast.error('Erreur lors du chargement de l\'offre');
        navigate('/rh/recrutements-generaux');
      }
    } catch (error) {
      console.error('Error fetching offer details:', error);
      toast.error('Erreur de connexion');
      navigate('/rh/recrutements-generaux');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/candidatures/job/${offerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const candidaturesData = await response.json();
        setCandidatures(candidaturesData);
      }
    } catch (error) {
      console.error('Error fetching candidatures:', error);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToRecruitments = () => {
    navigate('/rh/recrutements-generaux');
  };

  const handleAddCandidature = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleCandidatureAdded = () => {
    setShowAddModal(false);
    fetchCandidatures(); // Refresh candidatures list
  };

  const handleArchiveOffer = async () => {
    setValidating(false); // Reset validating state when opening modal
    setShowValidationModal(true);
  };

  const handleConfirmValidation = async () => {
    if (!validationPassword.trim()) {
      toast.error('Veuillez saisir votre mot de passe');
      return;
    }

    if (validating) {
      return; // Prevent multiple clicks
    }

    setValidating(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offerId}/archive`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: validationPassword
        }),
      });

      if (response.ok) {
        toast.success('Offre archivée avec succès');
        setShowValidationModal(false);
        setValidationPassword('');
        navigate('/rh/recrutements-generaux');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de l\'archivage');
      }
    } catch (error) {
      console.error('Error archiving offer:', error);
      toast.error('Erreur de connexion');
    } finally {
      setValidating(false);
    }
  };

  const handleDownloadFile = async (filename, type) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/download/${filename}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Erreur lors du téléchargement du fichier');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Erreur de connexion lors du téléchargement');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isRecentlyUpdated = (candidature) => {
    if (!candidature.updated_at) return false;
    const updateTime = new Date(candidature.updated_at);
    const now = new Date();
    const diffInMinutes = (now - updateTime) / (1000 * 60);
    return diffInMinutes <= 30; // Consider recent if updated in last 30 minutes
  };

  const getStatusStyle = (candidature) => {
    let baseClass = 'candidature-status-compact';
    
    switch (candidature.status) {
      case 'accepte':
        baseClass += ' status-accepte';
        break;
      case 'refuse':
        baseClass += ' status-refuse';
        break;
      default:
        baseClass += ' status-nouveau';
    }
    
    if (isRecentlyUpdated(candidature)) {
      baseClass += ' recently-updated';
    }
    
    return baseClass;
  };

  const getStatusText = (candidature) => {
    const isRecent = isRecentlyUpdated(candidature);
    
    switch (candidature.status) {
      case 'accepte':
        return isRecent ? 'Accepté (Nouveau)' : 'Accepté';
      case 'refuse':
        return isRecent ? 'Refusé (Nouveau)' : 'Refusé';
      default:
        return 'Nouveau';
    }
  };

  const handleAddToVivier = (candidature) => {
    setSelectedCandidature(candidature);
    setShowVivierModal(true);
  };

  const handleConfirmAddToVivier = async (commentaire) => {
    if (!selectedCandidature) return;
    
    try {
      const token = localStorage.getItem('token');

      // First, check if candidate already exists in vivier
      const existingCheck = await fetch(`${API}/candidatures/vivier/search?query=${encodeURIComponent(selectedCandidature.prenom + ' ' + selectedCandidature.nom)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (existingCheck.ok) {
        const existingCandidates = await existingCheck.json();
        // Check if same name already exists (case insensitive)
        const isDuplicate = existingCandidates.some(existing => 
          existing.prenom.toLowerCase() === selectedCandidature.prenom.toLowerCase() &&
          existing.nom.toLowerCase() === selectedCandidature.nom.toLowerCase()
        );

        if (isDuplicate) {
          toast.error('la candidature est déjà dans le vivier des candidatures');
          return;
        }
      }

      // Use custom comment if provided, otherwise use default
      const finalCommentaire = commentaire || `Ajouté depuis l'offre: ${offer?.title}. Commentaire original: ${selectedCandidature.commentaire || 'Aucun'}`;

      // If no duplicate, proceed with adding to vivier
      const response = await fetch(`${API}/candidatures/vivier`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prenom: selectedCandidature.prenom,
          nom: selectedCandidature.nom,
          intitule_poste: offer?.title || 'N/A',
          societe: offer?.company || 'N/A',
          email: selectedCandidature.email,
          telephone: selectedCandidature.telephone,
          commentaire: finalCommentaire,
          cv_filename: selectedCandidature.cv_filename,
          lettre_filename: selectedCandidature.lettre_filename,
        }),
      });

      if (response.ok) {
        toast.success(`${selectedCandidature.prenom} ${selectedCandidature.nom} a été ajouté(e) au vivier des candidatures`);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de l\'ajout au vivier');
      }
    } catch (error) {
      console.error('Error adding to vivier:', error);
      toast.error('Erreur de connexion');
    }
  };

  // Mock candidatures data (would come from API in real implementation)
  const mockCandidatures = [
    {
      id: '1',
      nom: 'Dupont',
      prenom: 'Jean',
      cv: 'cv_jean_dupont.pdf',
      lettre: 'lettre_jean_dupont.pdf',
      email: 'jean.dupont@email.com',
      telephone: '0123456789',
      commentaire: 'Profil intéressant, expérience solide',
      statut: 'En cours d\'évaluation'
    },
    {
      id: '2',
      nom: 'Martin',
      prenom: 'Sophie',
      cv: 'cv_sophie_martin.pdf',
      lettre: 'lettre_sophie_martin.pdf',
      email: 'sophie.martin@email.com',
      telephone: '0987654321',
      commentaire: 'Candidature prometteuse',
      statut: 'Entretien programmé'
    },
    {
      id: '3',
      nom: 'Bernard',
      prenom: 'Pierre',
      cv: 'cv_pierre_bernard.pdf',
      lettre: 'lettre_pierre_bernard.pdf',
      email: 'pierre.bernard@email.com',
      telephone: '0147258369',
      commentaire: 'À recontacter',
      statut: 'En attente'
    }
  ];

  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-destructive mb-4">Offre non trouvée</h1>
          <Button onClick={handleBackToRecruitments}>Retour aux recrutements</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="job-offer-detail-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToRecruitments}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos[offer.company]}
            alt={`Logo ${offer.company}`}
            className="rh-header-logo"
          />
        </div>
        <h1>Détail de l'offre d'emploi</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {/* Offer Header */}
        <div className="offer-detail-header-compact">
          <h2>{offer.company} : {offer.title}</h2>
          <div className="offer-detail-info-line">
            <div className="offer-detail-info-item-compact">
              <User className="offer-detail-info-icon-compact" />
              <span>{offer.creator_name || 'Inconnu'}</span>
            </div>
            <div className="offer-detail-info-separator">•</div>
            <div className="offer-detail-info-item-compact">
              <Calendar className="offer-detail-info-icon-compact" />
              <span>{formatDate(offer.date_arrivee)}</span>
            </div>
            <div className="offer-detail-info-separator">•</div>
            <div className="offer-detail-info-item-compact">
              <MapPin className="offer-detail-info-icon-compact" />
              <span>{offer.lieu_travail || 'Non spécifié'}</span>
            </div>
            {offer.type_poste && (
              <>
                <div className="offer-detail-info-separator">•</div>
                <div className="offer-detail-info-item-compact">
                  <span>{offer.type_poste}</span>
                </div>
              </>
            )}
            {offer.taux_horaire && (
              <>
                <div className="offer-detail-info-separator">•</div>
                <div className="offer-detail-info-item-compact">
                  <DollarSign className="offer-detail-info-icon-compact" />
                  <span>{offer.taux_horaire}€/h</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Offer Description */}
        <Card className="offer-description-card-compact">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold mb-3">Description détaillée</h3>
            <div className="offer-description-content-compact">
              <div className="mb-3">
                <h4 className="font-medium mb-1 text-sm text-primary">Missions</h4>
                <p className="text-gray-700 text-sm whitespace-pre-wrap">{offer.description}</p>
              </div>
              {offer.qualites_attendues && (
                <div>
                  <h4 className="font-medium mb-1 text-sm text-primary">Qualités attendues</h4>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{offer.qualites_attendues}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Candidatures Table */}
        <Card className="candidatures-card-compact">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">
                Candidatures ({candidatures.length})
              </h3>
              <div className="flex space-x-2">
                <Button
                  onClick={handleAddCandidature}
                  className="btn-primary text-sm px-3 py-2"
                  style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
                >
                  <Plus size={16} className="mr-2" />
                  Ajouter une candidature
                </Button>
                {user?.role === 'rh' && offer?.status === 'en_cours' && (
                  <Button
                    onClick={handleArchiveOffer}
                    variant="outline"
                    className="text-sm px-3 py-2"
                    style={{ borderColor: '#dc2626', color: '#dc2626' }}
                  >
                    <Archive size={16} className="mr-2" />
                    Archiver
                  </Button>
                )}
              </div>
            </div>
            <div className="candidatures-table-container-compact">
              <table className="candidatures-table-compact">
                <thead>
                  <tr>
                    <th>Candidat</th>
                    <th>Documents</th>
                    <th>Contact</th>
                    <th>Commentaire</th>
                    <th>Statut</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {candidatures.map((candidature) => (
                    <tr 
                      key={candidature.id}
                      className={isRecentlyUpdated(candidature) ? 'recently-updated-row' : ''}
                    >
                      <td>
                        <div className="candidature-name-compact">
                          {candidature.prenom} {candidature.nom}
                          {isRecentlyUpdated(candidature) && (
                            <span className="new-badge">NOUVEAU</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-files-compact">
                          {candidature.cv_filename ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mr-1 text-xs px-2 py-1"
                              onClick={() => handleDownloadFile(candidature.cv_filename, 'CV')}
                            >
                              <Download size={12} className="mr-1" />
                              Télécharger CV
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mr-1 text-xs px-2 py-1"
                              disabled
                            >
                              <Download size={12} className="mr-1" />
                              Fichier indisponible
                            </Button>
                          )}
                          {candidature.lettre_filename ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs px-2 py-1"
                              onClick={() => handleDownloadFile(candidature.lettre_filename, 'Lettre')}
                            >
                              <FileText size={12} className="mr-1" />
                              Télécharger Lettre
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs px-2 py-1"
                              disabled
                            >
                              <FileText size={12} className="mr-1" />
                              Fichier indisponible
                            </Button>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-contact-compact">
                          {/* Show manager coordinates if accepted, otherwise show original coordinates */}
                          {(candidature.manager_email || candidature.email) && (
                            <div className="contact-item-compact">
                              <Mail size={12} />
                              <span>{candidature.manager_email || candidature.email}</span>
                            </div>
                          )}
                          {(candidature.manager_telephone || candidature.telephone) && (
                            <div className="contact-item-compact">
                              <Phone size={12} />
                              <span>{candidature.manager_telephone || candidature.telephone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-comment-compact">
                          {candidature.commentaire || '-'}
                          {candidature.raison_refus && (
                            <div className="refus-reason">
                              <strong>Refus:</strong> {candidature.raison_refus}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={getStatusStyle(candidature)}>
                          {getStatusText(candidature)}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-actions-compact">
                          <Button
                            variant="outline"
                            size="sm"
                            className="btn-add-to-vivier"
                            onClick={() => handleAddToVivier(candidature)}
                            title="Ajouter cette candidature au vivier"
                          >
                            <Users size={12} className="mr-1" />
                            Ajouter au vivier
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {candidatures.length === 0 && (
              <div className="empty-state-compact">
                <p>Aucune candidature pour cette offre</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Candidature Modal */}
      {showAddModal && (
        <AddCandidatureModal
          isOpen={showAddModal}
          onClose={handleCloseAddModal}
          onSuccess={handleCandidatureAdded}
          jobOfferId={offerId}
        />
      )}

      {/* Add To Vivier Modal */}
      {showVivierModal && selectedCandidature && (
        <AddToVivierModal
          isOpen={showVivierModal}
          onClose={() => setShowVivierModal(false)}
          onConfirm={handleConfirmAddToVivier}
          candidatureName={`${selectedCandidature.prenom} ${selectedCandidature.nom}`}
        />
      )}

      {/* Validation Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-90vw">
            <h3 className="text-lg font-semibold mb-4">Archiver l'offre</h3>
            <p className="text-gray-600 mb-4">
              Pour archiver cette offre, veuillez saisir votre mot de passe RH :
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe RH
              </label>
              <input
                type="password"
                value={validationPassword}
                onChange={(e) => setValidationPassword(e.target.value)}
                disabled={validating}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  validating ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                placeholder="Saisissez votre mot de passe"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !validating) {
                    handleConfirmValidation();
                  }
                }}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                onClick={() => {
                  setShowValidationModal(false);
                  setValidationPassword('');
                  setValidating(false);
                }}
                variant="outline"
                disabled={validating}
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirmValidation}
                disabled={validating}
                className={`text-white ${validating 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {validating ? 'Archivage en cours...' : 'Archiver'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobOfferDetailPage;